import { Analytics } from "./types";

type HubSpotIdentifyParams = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

type HubSpotTrackParams = {
  name: string;
  properties?: Record<string, any>;
};

type HubSpotQueueItem =
  | ["trackPageView"]
  | ["setPath", string]
  | ["identify", HubSpotIdentifyParams]
  | ["trackCustomBehavioralEvent", HubSpotTrackParams];

type HubSpotQueue = Array<HubSpotQueueItem>;

declare global {
  interface Window {
    _hsq: HubSpotQueue;
  }
}

export class HubspotSourceProvider {
  private hsq: HubSpotQueue = [];

  initialize() {
    window._hsq = window._hsq || [];
    this.hsq = window._hsq;

    return HubspotAnalytics(this.hsq);
  }
}

export interface HubspotClient extends HubspotClientOptions {
  provider: "hubspot";
}

// HubSpot accounts live in a region; the tracking-loader host carries the
// region as a subdomain suffix, except the default (na1) which has none.
export type HubspotRegion = "na1" | "eu1" | "na2";

export interface HubspotClientOptions {
  sendPageView?: boolean;
  // Tracking-script loader fields. When `portalId` is set for the active
  // environment, `usePartnerAnalytics` injects the HubSpot tracking loader once
  // via `loadHubspotScript`. Omit `portalId` to load no script.
  portalId?: string;
  region?: HubspotRegion;
}

export function makeHubspotClient(args?: HubspotClientOptions): HubspotClient {
  return {
    provider: "hubspot",
    sendPageView: args?.sendPageView ?? true,
  };
}

const HUBSPOT_SCRIPT_ID = "hs-script-loader";
const HUBSPOT_COOKIE_STYLE_ID = "hs-cookie-hide-style";

// On mobile, HubSpot's cookie banner overlaps the booking UI; slide it off the
// bottom of the screen after a short delay.
const hubspotCookieHideCSS = `
@media (max-width:768px) {
  .hs-cookie-notification-position-bottom{
    bottom: -200px;
    transition: bottom 0.5s ease-in;
    transition-delay: 5s;
  }
}
`;

function hubspotScriptSrc(portalId: string, region?: HubspotRegion): string {
  const host =
    region && region !== "na1"
      ? `js-${region}.hs-scripts.com`
      : "js.hs-scripts.com";
  return `//${host}/${portalId}.js`;
}

// Inject the HubSpot tracking loader (and its mobile cookie-hide CSS) into the
// document head exactly once. Idempotency keys on the standard
// `hs-script-loader` id that any HubSpot embed uses, so the loader and a
// page-level embed of the same portal can never both inject the script.
export function loadHubspotScript({
  portalId,
  region,
}: {
  portalId: string;
  region?: HubspotRegion;
}): void {
  if (document.getElementById(HUBSPOT_SCRIPT_ID)) {
    return;
  }

  if (!document.getElementById(HUBSPOT_COOKIE_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = HUBSPOT_COOKIE_STYLE_ID;
    style.type = "text/css";
    style.textContent = hubspotCookieHideCSS;
    document.head.appendChild(style);
  }

  const script = document.createElement("script");
  script.id = HUBSPOT_SCRIPT_ID;
  script.type = "text/javascript";
  script.async = true;
  script.defer = true;
  script.src = hubspotScriptSrc(portalId, region);
  document.head.appendChild(script);
}

function HubspotAnalytics(_hsq: HubSpotQueue): Analytics {
  return {
    group: () => {},
    register: () => {},
    identify: (userID, traits) => {
      _hsq.push([
        "identify",
        {
          ...(userID && { id: userID }),
          ...(traits?.firstName && { firstName: traits?.firstName }),
          ...(traits?.lastName && { lastName: traits?.lastName }),
          ...(traits?.email && { email: traits?.email }),
        },
      ]);
      _hsq.push(["trackPageView"]);
    },
    screen: () => {
      _hsq.push(["setPath", window.location.pathname + window.location.search]);
      _hsq.push(["trackPageView"]);
    },
    ecommerceEvent: (eventName) => {
      if (_hsq) {
        // TODO: implement ecommerce tracking for hubspot
      }
    },
    event: (eventName, props) => {
      _hsq.push([
        "trackCustomBehavioralEvent",
        {
          name: eventName,
          properties: props,
        },
      ]);
    },
  };
}
